/* ARSYNC Control total
=========================================================================================
* Descripción: Botón de micrófono con dictado "push-to-talk" para los buscadores.
*              Se mantiene presionado (dedo o ratón) mientras se habla y al soltar se
*              detiene la escucha; el texto se entrega al consumidor en ese momento.
*              - onPointerDown inicia y captura el puntero (setPointerCapture) para
*                recibir el pointerup aunque el dedo se deslice fuera del botón.
*              - onPointerUp / onPointerCancel / onLostPointerCapture detienen.
*              - preventDefault en pointerdown y contextmenu para que no salte el
*                menú contextual ni el scroll del táctil mientras se habla.
*              - Teclado: Espacio/Enter mantienen y sueltan (onKeyDown/onKeyUp), y
*                onBlur detiene, para no dejar el micrófono abierto.
*              - onToggle queda como respaldo para consumidores que aún no usen
*                onStart/onStop.
*              Si el navegador no soporta Web Speech API o no hay permiso de micrófono,
*              el botón no se renderiza (el consumidor decide con isSupported/hasPermission).
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 09/10/2026 | Lic. José Luis Aragón C.        | Creación
*      2       | 10/10/2026 | Lic. José Luis Aragón C.        | Push-to-talk: escucha mientras se mantiene presionado y entrega al soltar
* ---------------------------------------------------------------------------------------
*/

import { IconButton, Tooltip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

const TECLAS_MANTENIDAS = [" ", "Spacebar", "Enter"];

const BotonVoz = ({ isSupported, hasPermission, isListening, onStart, onStop, onToggle }) => {
  if (!isSupported || !hasPermission) return null;

  const iniciar = () => {
    if (onStart) onStart();
    else onToggle?.(true);
  };

  const detener = () => {
    if (onStop) onStop();
    else onToggle?.(false);
  };

  return (
    <Tooltip title={isListening ? "Suelte para enviar" : "Mantenga presionado para hablar"}>
      <IconButton
        aria-label={isListening ? "Soltar para enviar el dictado" : "Mantener presionado para dictar"}
        color={isListening ? "error" : "primary"}
        onPointerDown={(e) => {
          if (e.pointerType === "mouse" && e.button !== 0) return;
          e.preventDefault();
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            // sin captura de puntero: se apoya en el pointerup sobre el botón
          }
          iniciar();
        }}
        onPointerUp={detener}
        onPointerCancel={detener}
        onLostPointerCapture={detener}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.repeat || !TECLAS_MANTENIDAS.includes(e.key)) return;
          e.preventDefault();
          iniciar();
        }}
        onKeyUp={(e) => {
          if (!TECLAS_MANTENIDAS.includes(e.key)) return;
          e.preventDefault();
          detener();
        }}
        onBlur={detener}
        sx={{
          cursor: isListening ? "grabbing" : "pointer",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          animation: isListening ? "pulse 1.5s infinite" : "none",
          "@keyframes pulse": {
            "0%": { transform: "scale(1)" },
            "50%": { transform: "scale(1.2)" },
            "100%": { transform: "scale(1)" },
          },
        }}
      >
        {isListening ? <MicOffIcon /> : <MicIcon />}
      </IconButton>
    </Tooltip>
  );
};

export default BotonVoz;